import { useCallback, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { useOrganisation } from '@/contexts/OrganisationContext';
import { supabase } from '@/integrations/supabase/client';
import { User, Stethoscope, FileText, Activity, TestTube, ArrowLeft, Edit2, Loader2, MessageSquare, CheckCircle, ArrowRight, Award, AlertTriangle, XCircle, DollarSign, BookOpen, ListChecks, Target, Copy, History, LockKeyhole } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { formatDisplayContent } from '@/lib/utils';
import { canManageEnterpriseAgent } from '@/lib/api/agentBranchAccess';

// Dynamic tab configuration based on agent type
const getTabsForType = (type: string) => {
  switch (type) {
    case 'examiner':
      return [
        { id: 'instruction', label: 'Instructions', icon: User },
        { id: 'case_summary', label: 'Case Summary', icon: FileText },
        { id: 'key_questions', label: 'Key Questions', icon: MessageSquare },
        { id: 'expected_answers', label: 'Expected Answers', icon: CheckCircle },
        { id: 'followup_probes', label: 'Follow-up Probes', icon: ArrowRight },
        { id: 'pass_criteria', label: 'Pass Criteria', icon: Award },
      ];
    case 'customer':
      return [
        { id: 'instruction', label: 'Instructions', icon: User },
        { id: 'scenario_context', label: 'Scenario Context', icon: FileText },
        { id: 'pain_points', label: 'Pain Points', icon: AlertTriangle },
        { id: 'common_objections', label: 'Objections', icon: XCircle },
        { id: 'buying_criteria', label: 'Buying Criteria', icon: DollarSign },
      ];
    case 'coach':
      return [
        { id: 'instruction', label: 'Instructions', icon: User },
        { id: 'coaching_approach', label: 'Coaching Approach', icon: Target },
        { id: 'session_structure', label: 'Session Structure', icon: ListChecks },
        { id: 'knowledge_base', label: 'Knowledge Base', icon: BookOpen },
        { id: 'evaluation_criteria', label: 'Evaluation Criteria', icon: Award },
      ];
    default: // patient
      return [
        { id: 'instruction', label: 'Instruction', icon: User },
        { id: 'presenting_complaint', label: 'Presenting Complaint', icon: Stethoscope },
        { id: 'medical_history', label: 'Medical History', icon: FileText },
        { id: 'physical_assessment', label: 'Physical Assessment', icon: Activity },
        { id: 'tests_scans', label: 'Tests and Scans', icon: TestTube },
      ];
  }
};

// Dynamic profile fields based on agent type
const getProfileFieldsForType = (agent: any) => {
  switch (agent.type) {
    case 'examiner':
      return [
        { label: 'Specialty', value: formatExaminerSpecialty(agent.clinical_specialty) },
        { label: 'Exam Type', value: formatExamType(agent.examination_type) },
        { label: 'Station', value: agent.station_title },
        { label: 'Style', value: formatExaminerStyle(agent.examiner_personality) },
        { label: 'Experience', value: agent.experience ? `${agent.experience} years` : null },
        { label: 'Location', value: agent.location },
      ];
    case 'customer':
      return [
        { label: 'Role', value: agent.prospect_role },
        { label: 'Company', value: agent.company_organization },
        { label: 'Industry', value: agent.industry },
        { label: 'Budget Authority', value: agent.budget_authority },
        { label: 'Occupation', value: agent.occupation },
        { label: 'Location', value: agent.location },
      ];
    case 'coach':
      const coachDuration = agent.max_session_duration_seconds 
        ? `${Math.round(agent.max_session_duration_seconds / 60)} min`
        : agent.estimated_duration ? `${agent.estimated_duration} min` : null;
      return [
        { label: 'Role', value: agent.occupation || agent.title },
        { label: 'Coaching Style', value: formatCoachingStyle(agent.coaching_style) },
        { label: 'Duration', value: coachDuration },
        { label: 'Output', value: agent.document_format },
        { label: 'Category', value: agent.agent_category },
      ];
    default: // patient
      return [
        { label: 'Age', value: agent.age },
        { label: 'Gender', value: agent.gender },
        { label: 'BMI', value: agent.bmi?.toFixed(2) },
        { label: 'Weight', value: agent.weight ? `${agent.weight} kg` : null },
        { label: 'Height', value: agent.height ? `${agent.height} cm` : null },
        { label: 'Role', value: agent.occupation },
      ];
  }
};

// Helper functions to format database values to readable labels
const formatExamType = (type: string) => {
  const types: Record<string, string> = {
    'osce': 'OSCE Station',
    'viva': 'Viva Voce / Oral Exam',
    'case_presentation': 'Case Presentation',
    'clinical_reasoning': 'Clinical Reasoning',
    'communication_skills': 'Communication Skills',
    'procedural': 'Procedural Skills',
    'ethics': 'Ethics & Professionalism',
    'emergency': 'Emergency Scenario'
  };
  return types[type] || type;
};

const formatExaminerSpecialty = (specialty: string) => {
  const specialties: Record<string, string> = {
    'general_practice': 'General Practice',
    'internal_medicine': 'Internal Medicine',
    'surgery': 'Surgery',
    'pediatrics': 'Pediatrics',
    'emergency_medicine': 'Emergency Medicine',
    'cardiology': 'Cardiology',
    'psychiatry': 'Psychiatry',
    'obstetrics_gynecology': 'Obstetrics & Gynecology',
    'orthopedics': 'Orthopedics',
    'radiology': 'Radiology'
  };
  return specialties[specialty] || specialty;
};

const formatExaminerStyle = (style: string) => {
  const styles: Record<string, string> = {
    'supportive': 'Supportive & Encouraging',
    'neutral': 'Neutral & Professional',
    'challenging': 'Challenging & Probing',
    'strict': 'Strict & Formal',
    'socratic': 'Socratic'
  };
  return styles[style] || style;
};

const formatAgentType = (type: string) => {
  const types: Record<string, string> = {
    'patient': 'Patient',
    'customer': 'Customer',
    'examiner': 'Examiner',
    'coach': 'Coach'
  };
  return types[type] || type;
};

const formatCoachingStyle = (style: string) => {
  const styles: Record<string, string> = {
    'challenger': 'Challenging (Direct & Pushing)',
    'consultative': 'Consultative (Collaborative)',
    'socratic': 'Socratic (Questioning)'
  };
  return styles[style] || style || 'Consultative';
};

interface ProfileRowProps {
  label: string;
  value: string | number | null | undefined;
}

const ProfileRow = ({ label, value }: ProfileRowProps) => (
  <div className="flex justify-between items-center py-2 border-b border-border last:border-0">
    <span className="text-muted-foreground">{label}:</span>
    <span className="font-medium text-foreground">{value || '-'}</span>
  </div>
);

export default function AgentProfileView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isMasterTenant, tenant } = useTenant();
  const {
    activeAssignment,
    canCreateAssets,
    hasEnterpriseHierarchy,
    isLoading: organisationLoading,
  } = useOrganisation();
  const [agent, setAgent] = useState<any>(null);
  const [sourceAgentHistory, setSourceAgentHistory] = useState<{
    id: string;
    name: string;
    completions: number;
    avg_score: number | null;
    status: string;
  } | null>(null);
  const [canManageAgent, setCanManageAgent] = useState(false);
  const [activeTab, setActiveTab] = useState('instruction');
  const [loading, setLoading] = useState(true);

  const fetchAgent = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('simulation_agents')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      if (data) {
        setSourceAgentHistory(null);
        if (data.source_agent_id) {
          const { data: sourceHistory, error: sourceHistoryError } = await supabase
            .from('simulation_agents')
            .select('id, name, completions, avg_score, status')
            .eq('id', data.source_agent_id)
            .maybeSingle();
          if (sourceHistoryError) {
            console.warn('Unable to load retained source-agent history:', sourceHistoryError);
          } else if (sourceHistory) {
            setSourceAgentHistory(sourceHistory);
          }
        }
        const canManage = isMasterTenant
          || !hasEnterpriseHierarchy
          || (data.is_system !== true && await canManageEnterpriseAgent(tenant.tenant_id, data.id));
        setCanManageAgent(canManage);
        setAgent(data);
      }
    } catch (error: any) {
      console.error('Error fetching agent:', error);
      toast.error('Failed to load agent profile');
      navigate('/admin/agents');
    } finally {
      setLoading(false);
    }
  }, [hasEnterpriseHierarchy, id, isMasterTenant, navigate, tenant.tenant_id]);

  useEffect(() => {
    if (organisationLoading) return;
    fetchAgent();
  }, [fetchAgent, organisationLoading]);

  const formatPhysicalAssessments = (data: any) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return '<p class="text-muted-foreground">No physical assessments available</p>';
    }
    return data.map((assessment: any) => `
      <div class="mb-4 p-4 bg-muted/50 rounded-lg">
        <h4 class="font-semibold text-foreground mb-2">${assessment.name || assessment.type || 'Assessment'}</h4>
        ${assessment.findings ? `<p class="text-sm text-muted-foreground">${assessment.findings}</p>` : ''}
        ${assessment.description ? `<p class="text-sm text-muted-foreground">${assessment.description}</p>` : ''}
      </div>
    `).join('');
  };

  const formatDiagnosticTests = (data: any) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return '<p class="text-muted-foreground">No diagnostic tests or scans available</p>';
    }
    return data.map((test: any) => `
      <div class="mb-4 p-4 bg-muted/50 rounded-lg">
        <h4 class="font-semibold text-foreground mb-2">${test.name || 'Test'}</h4>
        ${test.description ? `<p class="text-sm text-muted-foreground mb-2">${test.description}</p>` : ''}
        ${test.fileUrl ? `<a href="${test.fileUrl}" target="_blank" rel="noopener noreferrer" class="text-sm text-primary hover:underline">View Result</a>` : ''}
      </div>
    `).join('');
  };

  const formatKnowledgeSources = (data: any) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return '<p class="text-muted-foreground">No knowledge sources available</p>';
    }
    return data.map((source: any) => `
      <div class="mb-4 p-4 bg-muted/50 rounded-lg">
        <h4 class="font-semibold text-foreground mb-2">${source.name || 'Document'}</h4>
        ${source.url ? `<a href="${source.url}" target="_blank" rel="noopener noreferrer" class="text-sm text-primary hover:underline">View Document</a>` : ''}
        ${source.word_count ? `<p class="text-xs text-muted-foreground mt-1">${source.word_count} words</p>` : ''}
      </div>
    `).join('');
  };

  const formatEvaluationRubric = (data: any) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return '<p class="text-muted-foreground">No evaluation criteria available</p>';
    }
    return `<div class="space-y-3">${data.map((criterion: any) => `
      <div class="p-4 bg-muted/50 rounded-lg">
        <div class="flex justify-between items-start mb-2">
          <h4 class="font-semibold text-foreground">${criterion.criterion || criterion.name || 'Criterion'}</h4>
          ${criterion.weight ? `<span class="text-xs bg-primary/10 text-primary px-2 py-1 rounded">${criterion.weight}%</span>` : ''}
        </div>
        ${criterion.description ? `<p class="text-sm text-muted-foreground">${criterion.description}</p>` : ''}
      </div>
    `).join('')}</div>`;
  };

  const noDataMessage = (field: string) => `<p class="text-muted-foreground">No ${field} available</p>`;

  const getTabContent = () => {
    if (!agent) return '';
    
    switch (activeTab) {
      // Common
      case 'instruction':
        return formatDisplayContent(agent.instructions) || noDataMessage('instructions');
      
      // Examiner tabs
      case 'case_summary':
        return formatDisplayContent(agent.case_summary) || noDataMessage('case summary');
      case 'key_questions':
        return formatDisplayContent(agent.key_questions) || noDataMessage('key questions');
      case 'expected_answers':
        return formatDisplayContent(agent.expected_answers) || noDataMessage('expected answers');
      case 'followup_probes':
        return formatDisplayContent(agent.followup_probes) || noDataMessage('follow-up probes');
      case 'pass_criteria':
        return formatDisplayContent(agent.pass_criteria) || noDataMessage('pass criteria');
      
      // Customer tabs
      case 'scenario_context':
        return formatDisplayContent(agent.scenario_context) || noDataMessage('scenario context');
      case 'pain_points':
        return formatDisplayContent(agent.pain_points) || noDataMessage('pain points');
      case 'common_objections':
        return formatDisplayContent(agent.common_objections) || noDataMessage('objections');
      case 'buying_criteria':
        return formatDisplayContent(agent.buying_criteria) || noDataMessage('buying criteria');
      
      // Coach tabs
      case 'coaching_approach':
        return formatDisplayContent(agent.backstory) || noDataMessage('coaching approach');
      case 'session_structure':
        return formatDisplayContent(agent.scenario_context) || noDataMessage('session structure');
      case 'knowledge_base':
        return formatKnowledgeSources(agent.knowledge_sources);
      case 'evaluation_criteria':
        return formatEvaluationRubric(agent.evaluation_rubric);
      
      // Patient tabs (existing)
      case 'presenting_complaint':
        return formatDisplayContent(agent.presenting_complaint) || noDataMessage('presenting complaint');
      case 'medical_history':
        return formatDisplayContent(agent.medical_history) || noDataMessage('medical history');
      case 'physical_assessment':
        return formatPhysicalAssessments(agent.physical_assessments);
      case 'tests_scans':
        return formatDiagnosticTests(agent.diagnostic_tests);
      
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="animate-spin h-12 w-12 mx-auto mb-4 text-primary" />
          <div className="text-lg text-muted-foreground">Loading profile...</div>
        </div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground mb-2">Agent Not Found</h2>
          <p className="text-muted-foreground mb-4">The agent you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/admin/agents')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Agents
          </Button>
        </div>
      </div>
    );
  }

  const tabs = getTabsForType(agent.type);
  const profileFields = getProfileFieldsForType(agent);

  const isSystemAgent = agent.is_system === true;
  const isTenantAdmin = !!user && (user.roles.includes('admin') || user.roles.includes('company_admin')) && !isMasterTenant;
  const canEdit = agent.status === 'active'
    && (isMasterTenant || (!isSystemAgent && canManageAgent));
  const duplicateScopeLabel = activeAssignment?.unit_name
    ? `${activeAssignment.branch_name} → ${activeAssignment.unit_name}`
    : activeAssignment?.branch_name || 'your organisation scope';
  const canDuplicate = agent.status === 'active' && (
    (isTenantAdmin && isSystemAgent)
      || (hasEnterpriseHierarchy && canCreateAssets && !canEdit)
  );

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header Actions */}
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/agents')}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Agents
          </Button>

          <div className="flex items-center gap-2">
            {canDuplicate && (
              <Button
                variant="default"
                size="sm"
                onClick={() => navigate(`/admin/agents/create?template=${id}${user?.company_id ? `&company=${user.company_id}` : ''}`)}
              >
                <Copy className="mr-2 h-4 w-4" />
                {hasEnterpriseHierarchy && !canEdit ? `Duplicate to ${duplicateScopeLabel}` : 'Duplicate'}
              </Button>
            )}

            {canEdit && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/admin/agents/${id}/edit`)}
              >
                <Edit2 className="mr-2 h-4 w-4" />
                Edit Agent
              </Button>
            )}
          </div>
        </div>

        {agent.status === 'active' && hasEnterpriseHierarchy && !isSystemAgent && !canEdit && (
          <div className="mb-6 flex items-start gap-3 rounded-xl border border-border bg-muted/50 p-4 text-sm text-foreground">
            <LockKeyhole className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
            <div>
              <p className="font-semibold">Global agent — view only</p>
              <p className="text-muted-foreground">
                Duplicate this agent into {duplicateScopeLabel} to make local changes without changing it for other branches.
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Profile Card */}
          <div className="lg:col-span-1">
            <Card className="rounded-2xl shadow-lg p-8">
              {/* Profile Image */}
              <div className="flex justify-center mb-6">
                <div className="w-48 h-48 rounded-full overflow-hidden border-4 border-white shadow-xl">
                  {agent.image_url ? (
                    <img 
                      src={agent.image_url} 
                      alt={agent.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-muted flex items-center justify-center">
                      <User className="w-20 h-20 text-muted-foreground" />
                    </div>
                  )}
                </div>
              </div>

              {/* Agent Name & Type Badge */}
              <div className="text-center mb-6">
                <h1 className="text-2xl font-bold text-foreground mb-2">
                  {agent.name}
                </h1>
                <span className="inline-block px-3 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full">
                  {formatAgentType(agent.type)}
                </span>
              </div>

              <div className="mb-6 grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-muted/50 p-3 text-center">
                  <div className="text-xl font-bold text-foreground">{agent.completions ?? 0}</div>
                  <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    {agent.source_agent_id ? 'Unit completions' : 'Completions'}
                  </div>
                </div>
                <div className="rounded-lg bg-muted/50 p-3 text-center">
                  <div className="text-xl font-bold text-primary">
                    {typeof agent.avg_score === 'number' ? `${Math.round(agent.avg_score)}%` : '—'}
                  </div>
                  <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                    {agent.source_agent_id ? 'Unit avg score' : 'Avg score'}
                  </div>
                </div>
              </div>

              {sourceAgentHistory
                && (sourceAgentHistory.completions > 0 || sourceAgentHistory.avg_score !== null) && (
                <button
                  type="button"
                  onClick={() => navigate(`/admin/agents/${sourceAgentHistory.id}/view`)}
                  className="mb-6 flex w-full items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-left text-blue-950 transition-colors hover:bg-blue-100 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-100 dark:hover:bg-blue-950/50"
                >
                  <History className="mt-0.5 h-4 w-4 shrink-0" />
                  <span className="text-xs">
                    <span className="block font-semibold">
                      {sourceAgentHistory.completions} historical completion{sourceAgentHistory.completions === 1 ? '' : 's'} retained
                    </span>
                    <span className="block text-blue-800 dark:text-blue-200">
                      View the archived original record
                    </span>
                  </span>
                </button>
              )}

              {agent.status !== 'active' && (
                <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
                  <p className="font-semibold">Archived historical agent</p>
                  <p className="mt-1 text-xs">
                    Its completed sessions remain here for accurate reporting. Active unit copies keep their own separate totals.
                  </p>
                </div>
              )}

              {/* Profile Details - Dynamic based on type */}
              <div className="space-y-3">
                {profileFields.map((field, index) => (
                  <ProfileRow key={index} label={field.label} value={field.value} />
                ))}
              </div>
            </Card>
          </div>

          {/* Right Column - Tabbed Content */}
          <div className="lg:col-span-2">
            <Card className="rounded-2xl shadow-lg overflow-hidden">
              {/* Tab Navigation */}
              <div className="border-b border-border">
                <nav className="flex overflow-x-auto">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-4 md:px-6 py-4 text-xs md:text-sm font-medium whitespace-nowrap transition-colors ${
                          activeTab === tab.id
                            ? 'text-primary border-b-2 border-primary bg-primary/5'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {tab.label}
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-6 md:p-8">
                <div 
                  className="prose prose-sm max-w-none prose-headings:text-foreground prose-p:text-muted-foreground prose-strong:text-foreground prose-ul:text-muted-foreground"
                  dangerouslySetInnerHTML={{ __html: getTabContent() }}
                />
              </div>
            </Card>
          </div>
        </div>

        {/* Decorative Avatar - Bottom Right */}
        {agent.image_url && (
          <div className="hidden lg:block fixed bottom-8 right-8 w-24 h-24 rounded-full overflow-hidden shadow-xl border-4 border-white opacity-80">
            <img 
              src={agent.image_url} 
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
        )}
      </div>
    </div>
  );
}
